import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  AreaChart, Area, CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { Wallet, FolderKanban, Gauge, Zap, Loader2 } from "lucide-react";

const COLORS = ["#F97316", "#0EA5E9", "#10B981", "#F59E0B", "#3B82F6", "#EF4444", "#A855F7", "#14B8A6"];

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="h-4 w-4 text-primary" />
        <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
      </div>
      <div className="text-2xl sm:text-3xl font-mono font-bold tracking-tight tabular-nums">{value}</div>
    </div>
  );
}

const cardBox = "rounded-xl border border-border bg-card p-5";
const TooltipBox = ({ active, payload, label, isMoney }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-sm shadow-xl">
      <div className="font-semibold">{label || payload[0].name}</div>
      <div className="font-mono text-primary">{isMoney ? money(payload[0].value, 0) : payload[0].value}</div>
    </div>
  ) : null;

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["analytics"], queryFn: async () => (await api.get("/analytics")).data });

  if (isLoading) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const t = data?.totals || {};
  const divisions = data?.cost_by_division || [];
  const statusData = data?.by_status || [];
  const scurve = data?.s_curve || [];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">Portfolio-wide performance across every bid.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Wallet} label="Portfolio Value" value={money(t.portfolio_value, 0)} testid="kpi-portfolio-value" />
        <Kpi icon={FolderKanban} label="Projects" value={t.project_count ?? 0} testid="kpi-projects" />
        <Kpi icon={Gauge} label="Avg Quality" value={`${t.avg_quality ?? 0}`} testid="kpi-quality" />
        <Kpi icon={Zap} label="Active Bids" value={t.active_bids ?? 0} testid="kpi-active-bids" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className={`${cardBox} lg:col-span-2`} data-testid="chart-cost-by-division">
          <h3 className="font-semibold mb-4">Cost by CSI Division</h3>
          {divisions.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={divisions} margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} angle={-20} textAnchor="end" height={70} interval={0} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<TooltipBox isMoney />} cursor={{ fill: "hsl(var(--secondary))" }} />
                <Bar dataKey="value" fill="#F97316" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={cardBox} data-testid="chart-by-status">
          <h3 className="font-semibold mb-4">Projects by Status</h3>
          {statusData.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<TooltipBox />} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-3 mt-2 justify-center">
            {statusData.map((s, i) => (
              <span key={s.name} className="flex items-center gap-1.5 text-xs capitalize">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{s.name} ({s.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className={cardBox} data-testid="chart-s-curve">
        <h3 className="font-semibold mb-4">Cumulative Cost S-Curve</h3>
        {scurve.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={scurve} margin={{ left: 10, right: 10 }}>
              <defs>
                <linearGradient id="scg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F97316" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#F97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="index" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<TooltipBox isMoney />} />
              <Area type="monotone" dataKey="cumulative" stroke="#F97316" strokeWidth={2} fill="url(#scg)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

const Empty = () => <div className="h-[260px] grid place-items-center text-sm text-muted-foreground">No data yet — create a project and generate a BOQ.</div>;
